import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { EchartGenericChartComponent } from '@/app/charts/echart-generic-chart/echart-generic-chart.component';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { ImageService } from '@/app/services/image/image.service';
import { ThemeService } from '@/app/services/theme/theme.service';
import {
  CreatorStatsAggregateResponseDto,
  CreatorStatsLeaderboardMetric,
  StatsCreatorLeaderboardModel,
  StatsFunnelBarModel,
  StatsKpiCardModel,
  StatsMetricChartModel,
  StatsPeriodSelection,
} from '../../admin-stats-aggregate/admin-stats-aggregate.types';
import {
  buildDailyBarChart,
  buildFunnelBars,
  chartVariantForMetricKey,
  creatorInitials,
  funnelBarClass,
  getKpiTrend,
  kpiAccentDotClass,
  mapTopCreatorRows,
  topCreatorBarClass,
} from '../../admin-stats-dashboard/admin-stats-dashboard.helpers';
import { formatStatsPeriodLabel } from '../../admin-stats-period/admin-stats-period.helpers';
import { AdminCreatorStatsService } from '../../../services/admin-creator-stats.service';
import { AdminStatsPeriodFilter } from '../../admin-stats-period-filter/admin-stats-period-filter';
import { CreatorStatsSortField } from '../creator-stats.types';

type LeaderboardSpec = {
  key: CreatorStatsLeaderboardMetric;
  title: string;
  subtitle: string;
  secondaryMetric: CreatorStatsLeaderboardMetric;
  secondarySuffix: string;
};

@Component({
  selector: 'admin-tool-creator-stats-dashboard',
  imports: [AdminStatsPeriodFilter, LoadingInlineComponent, EchartGenericChartComponent, LdgImgComponent],
  templateUrl: './creator-stats-dashboard.html',
  styleUrl: './creator-stats-dashboard.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorStatsDashboard implements OnDestroy {
  readonly topCreatorsLimit = 5;

  private readonly leaderboardSpecs: readonly LeaderboardSpec[] = [
    {
      key: 'appearedInSearches',
      title: 'Top searches',
      subtitle: 'By search appearances',
      secondaryMetric: 'profileVisits',
      secondarySuffix: 'visits',
    },
    {
      key: 'profileVisits',
      title: 'Top visited',
      subtitle: 'By profile visits',
      secondaryMetric: 'appearedInSearches',
      secondarySuffix: 'searches',
    },
    {
      key: 'invitedToBrief',
      title: 'Top invited',
      subtitle: 'By brief invites',
      secondaryMetric: 'profileVisits',
      secondarySuffix: 'visits',
    },
    {
      key: 'hiredForBrief',
      title: 'Top hired',
      subtitle: 'By hires',
      secondaryMetric: 'invitedToBrief',
      secondarySuffix: 'invites',
    },
  ];

  aggregate: CreatorStatsAggregateResponseDto | null = null;
  kpiCards: StatsKpiCardModel[] = [];
  metricCharts: StatsMetricChartModel[] = [];
  funnelBars: StatsFunnelBarModel[] = [];
  leaderboards: StatsCreatorLeaderboardModel[] = [];
  rangeLabel = '';
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  error: string | null = null;

  readonly creatorInitials = creatorInitials;
  readonly getKpiTrend = getKpiTrend;
  readonly kpiAccentDotClass = kpiAccentDotClass;
  readonly funnelBarClass = funnelBarClass;
  readonly topCreatorBarClass = topCreatorBarClass;

  private subscriptionList = new Subscription();
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly adminCreatorStatsService = inject(AdminCreatorStatsService);
  private readonly themeService = inject(ThemeService);
  readonly imageService = inject(ImageService);

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onPeriodChange(selection: StatsPeriodSelection): void {
    this.loadDashboard(selection.startDate, selection.endDate);
  }

  private loadDashboard(startDate: string, endDate: string): void {
    this.subscriptionList.unsubscribe();
    this.subscriptionList = new Subscription();
    this.loadingState = 'loading';
    this.error = null;
    this.refreshView();

    this.subscriptionList.add(
      forkJoin({
        aggregate: this.adminCreatorStatsService.aggregate({ startDate, endDate }),
        appearedInSearches: this.adminCreatorStatsService.list(
          this.buildLeaderboardFilter('appearedInSearches'),
        ),
        profileVisits: this.adminCreatorStatsService.list(
          this.buildLeaderboardFilter('profileVisits'),
        ),
        invitedToBrief: this.adminCreatorStatsService.list(
          this.buildLeaderboardFilter('invitedToBrief'),
        ),
        hiredForBrief: this.adminCreatorStatsService.list(
          this.buildLeaderboardFilter('hiredForBrief'),
        ),
      }).subscribe({
        next: ({ aggregate, appearedInSearches, profileVisits, invitedToBrief, hiredForBrief }) => {
          const leaderboardResults: Record<
            CreatorStatsLeaderboardMetric,
            { data: Parameters<typeof mapTopCreatorRows>[0] }
          > = {
            appearedInSearches,
            profileVisits,
            invitedToBrief,
            hiredForBrief,
          };

          this.aggregate = aggregate;
          this.rangeLabel = formatStatsPeriodLabel(aggregate.current.startDate, aggregate.current.endDate);
          this.kpiCards = this.buildKpiCards(aggregate);
          this.metricCharts = this.buildMetricCharts(aggregate);
          this.funnelBars = buildFunnelBars(aggregate.current.appearedInSearches, [
            { label: 'Profile visits', value: aggregate.current.profileVisits },
            { label: 'Brief invites', value: aggregate.current.invitedToBrief },
            { label: 'Hired', value: aggregate.current.hiredForBrief },
          ]);
          this.leaderboards = this.leaderboardSpecs.map((spec) => ({
            key: spec.key,
            title: spec.title,
            subtitle: spec.subtitle,
            secondarySuffix: spec.secondarySuffix,
            rows: mapTopCreatorRows(
              (leaderboardResults[spec.key]?.data ?? []).slice(0, this.topCreatorsLimit),
              spec.key,
              spec.secondaryMetric,
            ),
          }));
          this.loadingState = 'loaded';
          this.refreshView();
        },
        error: () => {
          this.aggregate = null;
          this.kpiCards = [];
          this.metricCharts = [];
          this.funnelBars = [];
          this.leaderboards = [];
          this.loadingState = 'error';
          this.error = 'Unable to load creator stats dashboard.';
          this.refreshView();
        },
      }),
    );
  }

  private buildLeaderboardFilter(sortField: CreatorStatsSortField) {
    return {
      take: this.topCreatorsLimit,
      skip: 0,
      sortField,
      sortOrder: 'DESC' as const,
    };
  }

  private buildKpiCards(data: CreatorStatsAggregateResponseDto): StatsKpiCardModel[] {
    return [
      {
        key: 'appearedInSearches',
        label: 'Search appearances',
        current: data.current.appearedInSearches,
        previous: data.previous.appearedInSearches,
        accent: 'violet',
      },
      {
        key: 'profileVisits',
        label: 'Profile visits',
        current: data.current.profileVisits,
        previous: data.previous.profileVisits,
        accent: 'violet',
      },
      {
        key: 'invitedToBrief',
        label: 'Brief invites',
        current: data.current.invitedToBrief,
        previous: data.previous.invitedToBrief,
        accent: 'pink',
      },
      {
        key: 'hiredForBrief',
        label: 'Hired',
        current: data.current.hiredForBrief,
        previous: data.previous.hiredForBrief,
        accent: 'pink',
      },
    ];
  }

  private buildMetricCharts(data: CreatorStatsAggregateResponseDto): StatsMetricChartModel[] {
    const specs = [
      {
        key: 'appearedInSearches',
        label: 'Search appearances',
        pick: (point: (typeof data.daily)[number]) => point.appearedInSearches,
      },
      {
        key: 'profileVisits',
        label: 'Profile visits',
        pick: (point: (typeof data.daily)[number]) => point.profileVisits,
      },
      {
        key: 'invitedToBrief',
        label: 'Brief invites',
        pick: (point: (typeof data.daily)[number]) => point.invitedToBrief,
      },
      {
        key: 'hiredForBrief',
        label: 'Hired',
        pick: (point: (typeof data.daily)[number]) => point.hiredForBrief,
      },
    ] as const;

    return specs.map((spec) => {
      const built = buildDailyBarChart(
        data.daily.map((point) => ({
          date: point.date,
          value: spec.pick(point),
        })),
      );

      return {
        key: spec.key,
        label: spec.label,
        chartOption: built.chartOption,
        chartXaxisData: built.chartXaxisData,
        hasData: built.hasData,
        chartVariant: chartVariantForMetricKey(spec.key, this.themeService.isDarkMode()),
      };
    });
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }
}
